import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Button, Box, useMediaQuery, useTheme, Stack, Paper, Typography, Chip, IconButton,
  FormControl, InputLabel, Select, MenuItem, Switch
} from '@mui/material';
import { MenuInicial } from "./MenuInicial";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, USERLIST, USERACTUALIZAR } from '../services/apiConfig';
import { DataGrid } from '@mui/x-data-grid';
import Cargando from './utils/Cargando.jsx';
import axios from 'axios';
import Person from '@mui/icons-material/Person';
import Edit from '@mui/icons-material/Edit';
import TituloPagina from './utils/TituloPagina.jsx';
import { avisarError } from '../services/toast';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const activo = (u) => Number(u.activo) === 1;
const bloqueado = (u) => Number(u.bloqueado) === 1;

function UserList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [estado, setEstado] = useState('activos');

  const userData = JSON.parse(sessionStorage.getItem("userData") || '{}');
  const propioId = userData?.usuarioid;

  const fetchUsers = useCallback(() => {
    setLoading(true);
    const token = sessionStorage.getItem("authToken");

    axios.get(API_BASE_URL + USERLIST, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then((response) => {
      const payload = response.data?.data ?? response.data;
      setUsers(Array.isArray(payload) ? payload : []);
      setLoading(false);
    })
    .catch((error) => {
      console.error('Error al cargar los usuarios', error);
      if (error.response?.status === 401) {
        navigate("/login");
      }
      setLoading(false);
    });
  }, [navigate]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const filtrados = useMemo(() => {
    return users.filter((u) => {
      if (estado === 'todos') return true;
      return estado === 'activos' ? activo(u) : !activo(u);
    });
  }, [users, estado]);

  const handleEdit = (user) => {
    navigate(`/user?userid=${user.id}`);
  };

  const toggleActivo = (u) => {
    if (u.id === propioId) return; // nunca desactivarse a uno mismo
    axios.post(API_BASE_URL + USERACTUALIZAR, {
      usuarioid: u.id, email: u.email, perfil_id: u.perfil,
      auditaroperaciones: u.auditaroperaciones, activo: activo(u) ? 0 : 1,
    }, { headers: authHeader() })
      .then((res) => {
        if (!res.data?.success) throw new Error(res.data?.message || 'Error al guardar.');
        fetchUsers();
      })
      .catch((e) => avisarError(e.response?.data?.message || e.message || 'Error al cambiar el estado.'));
  };

  const toggleBloqueado = (u) => {
    if (u.id === propioId) return;
    axios.post(API_BASE_URL + USERACTUALIZAR, {
      usuarioid: u.id, email: u.email, perfil_id: u.perfil,
      auditaroperaciones: u.auditaroperaciones, bloqueado: bloqueado(u) ? 0 : 1,
    }, { headers: authHeader() })
      .then((res) => {
        if (!res.data?.success) throw new Error(res.data?.message || 'Error al guardar.');
        fetchUsers();
      })
      .catch((e) => avisarError(e.response?.data?.message || e.message || 'Error al cambiar el bloqueo.'));
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'username', headerName: 'Usuario', width: 150 },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'perfil_nombre', headerName: 'Perfil', width: 120 },
    { field: 'sucursales_nombres', headerName: 'Sucursales', width: 200 },
    {
      field: 'activo', headerName: 'Activo', width: 90, align: 'center', headerAlign: 'center',
      renderCell: (params) => (
        <Switch
          size="small" checked={activo(params.row)} disabled={params.row.id === propioId}
          onChange={() => toggleActivo(params.row)}
        />
      ),
    },
    {
      field: 'bloqueado', headerName: 'Bloqueado', width: 110, align: 'center', headerAlign: 'center',
      renderCell: (params) => (
        <Switch
          size="small" color="error" checked={bloqueado(params.row)} disabled={params.row.id === propioId}
          onChange={() => toggleBloqueado(params.row)}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Button size="small" variant="outlined" onClick={() => handleEdit(params.row)}>Editar</Button>
      ),
    },
  ];

    return (
    <Container maxWidth={isMobile ? false : "xl"} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={Person}
          titulo="Usuarios"
          ayuda="Un usuario desactivado no puede iniciar sesión, aunque sus datos y asignaciones se conservan — se puede reactivar en cualquier momento. Después de 5 intentos fallidos de contraseña, el usuario se bloquea solo — un administrador o franquiciado lo puede desbloquear acá."
          accion={
            <Button variant="contained" color="primary" onClick={() => navigate('/user?mode=create')}>
              Nuevo Usuario
            </Button>
          }
        />

        <FormControl size="small" sx={{ minWidth: 150, mb: 2 }}>
          <InputLabel>Estado</InputLabel>
          <Select label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)}>
            <MenuItem value="activos">Activos</MenuItem>
            <MenuItem value="inactivos">Desactivados</MenuItem>
            <MenuItem value="todos">Todos</MenuItem>
          </Select>
        </FormControl>

        {loading ? (
          <Cargando />
        ) : isMobile ? (
          <Stack spacing={1}>
            {filtrados.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>Sin resultados.</Typography>
            )}
            {filtrados.map((u) => {
              const act = activo(u);
              const bloq = bloqueado(u);
              return (
                <Paper key={u.id} variant="outlined" onClick={() => handleEdit(u)}
                  sx={{ p: 1.2, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', opacity: act ? 1 : 0.6 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.username}</Typography>
                      {u.perfil_nombre && <Chip size="small" label={u.perfil_nombre} sx={{ height: 18 }} />}
                      {!act && <Chip size="small" label="Desactivado" color="default" sx={{ height: 18 }} />}
                      {bloq && <Chip size="small" label="Bloqueado" color="error" sx={{ height: 18 }} />}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{u.email || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{u.sucursales_nombres || 'Sin sucursales'}</Typography>
                  </Box>
                  <Switch size="small" checked={act} disabled={u.id === propioId} title="Activo"
                    onClick={(e) => e.stopPropagation()} onChange={() => toggleActivo(u)} />
                  <Switch size="small" color="error" checked={bloq} disabled={u.id === propioId} title="Bloqueado"
                    onClick={(e) => e.stopPropagation()} onChange={() => toggleBloqueado(u)} />
                  <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleEdit(u); }}><Edit fontSize="small" /></IconButton>
                </Paper>
              );
            })}
          </Stack>
        ) : (
          <div style={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={filtrados}
              columns={columns}
              pageSize={10}
              getRowId={(row) => row.id}
              getRowClassName={(params) => activo(params.row) ? '' : 'row-inactivo'}
              sx={{ '& .row-inactivo': { opacity: 0.55 } }}
            />
          </div>
        )}
      </Box>
    </Container>
  );
}

export default UserList;
